/** The pieces the cell pop-ups share, so they read and behave the same (see CounterAssignPopover, OfficerAssignPopover). */
import type { ReactNode } from 'react';
import { CELL_ACTION_LOOK, type CellActionKind } from '@/utils/cellAction';

/** Swatch, title and the × that closes without saving — the way out of a pop-up opened by an accidental tap. */
export function PopoverHeader({ title, color, busy, onClose }: { title: ReactNode; color: string; busy: boolean; onClose: () => void }) {
  return (
    <div style={{ fontSize: 13, fontWeight: 600, color, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: color, opacity: 0.8, flexShrink: 0 }} />
      {title}
      <button
        onClick={onClose}
        disabled={busy}
        title="Close without saving"
        aria-label="Close without saving"
        style={{
          marginLeft: 'auto', background: 'none', border: 'none', cursor: busy ? 'not-allowed' : 'pointer',
          color: '#94a3b8', fontSize: 20, lineHeight: 1, padding: '0 2px', flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}

/** One button per action the pop-up offers; the chosen one filled in its colour. */
export function ActionTabs({ actions, value, onChange, isMobile }: {
  actions: CellActionKind[];
  value: CellActionKind;
  onChange: (kind: CellActionKind) => void;
  isMobile: boolean;
}) {
  if (actions.length < 2) return null;
  return (
    <div role="tablist" style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
      {actions.map((kind) => {
        const { tab, color } = CELL_ACTION_LOOK[kind];
        const on = kind === value;
        return (
          <button
            key={kind}
            role="tab"
            aria-selected={on}
            data-testid={`cell-action-${kind}`}
            onClick={() => onChange(kind)}
            style={{
              flex: 1, padding: isMobile ? '11px 0' : '8px 0', borderRadius: 8, fontWeight: 600, fontSize: 13,
              cursor: 'pointer', border: '1.5px solid', background: on ? color : 'white', color: on ? 'white' : '#374151',
              borderColor: on ? color : '#d1d5db', transition: 'background 0.12s, color 0.12s',
            }}
          >
            {tab}
          </button>
        );
      })}
    </div>
  );
}

/** A tinted note saying what the chosen action will do. */
export function ActionNote({ color, children }: { color: string; children: ReactNode }) {
  return (
    <div style={{
      marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
      color, background: `${color}12`, border: `1.5px solid ${color}55`,
    }}>
      {children}
    </div>
  );
}

/** The error (if any) and the Save button, labelled and coloured for the chosen action. Dismissing is the header ×. */
export function PopoverFooter({ action, busy, disabled, error, onSave, isMobile }: {
  action: CellActionKind;
  busy: boolean;
  disabled?: boolean;
  error?: string;
  onSave: () => void;
  isMobile: boolean;
}) {
  const { save, busy: busyLabel, color } = CELL_ACTION_LOOK[action];
  const off = busy || !!disabled;
  return (
    <>
      {error && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          onClick={onSave}
          disabled={off}
          data-testid="cell-action-save"
          style={{
            padding: isMobile ? '11px 24px' : '8px 22px', background: color, opacity: off ? 0.55 : 1, color: 'white',
            border: 'none', borderRadius: 8, cursor: off ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700,
          }}
        >
          {busy ? busyLabel : save}
        </button>
      </div>
    </>
  );
}
